<template>
  <div class="container mt-5">
    <div class="card shadow-sm">
      <div class="card-header bg-primary text-white">
        <h4 class="mb-0">
          <i class="flaticon-payment me-2"></i>
          Choisir un moyen de paiement
        </h4>
      </div>

      <div class="card-body p-4">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
          <p class="mt-3">Chargement...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <div v-else>
          <div class="payment-info mb-4 p-3 bg-light rounded">
            <h5>Détails du paiement</h5>
            <p class="mb-1"><strong>Référence BESC:</strong> {{ refbesc }}</p>
            <p class="mb-1"><strong>Montant:</strong> {{ formatNumber(amount) }} FCFA</p>
          </div>

          <h5 class="mb-3">Sélectionnez votre méthode de paiement</h5>

          <div class="payment-methods">
            <!-- KKiaPay (Mobile Money + Cartes) - EN PREMIER -->
            <div 
              class="payment-method-card" 
              @click="selectMethod('kkiapay')"
              :class="{ 'selected': selectedMethod === 'kkiapay' }">
              <div class="method-icon">
                <i class="fas fa-mobile-alt" style="font-size: 50px; color: #00c853;"></i>
              </div>
              <div class="method-info">
                <h6 class="mb-1">Mobile Money / Carte</h6>
                <p class="text-muted small mb-0">
                  MTN, Moov, Visa, Mastercard
                </p>
                <span class="badge bg-success mt-1">Recommandé 🇧🇯</span>
              </div>
              <div class="method-check">
                <i v-if="selectedMethod === 'kkiapay'" class="fas fa-check-circle text-success"></i>
              </div>
            </div>

            <!-- PayPal -->
            <div 
              class="payment-method-card" 
              @click="selectMethod('paypal')"
              :class="{ 'selected': selectedMethod === 'paypal' }">
              <div class="method-icon">
                <i class="fab fa-paypal" style="font-size: 50px; color: #0070ba;"></i>
              </div>
              <div class="method-info">
                <h6 class="mb-1">PayPal</h6>
                <p class="text-muted small mb-0">
                  Payez avec votre compte PayPal
                </p>
              </div>
              <div class="method-check">
                <i v-if="selectedMethod === 'paypal'" class="fas fa-check-circle text-success"></i>
              </div>
            </div>

            <!-- Stripe (Cartes internationales) -->
            <div 
              class="payment-method-card" 
              @click="selectMethod('stripe')"
              :class="{ 'selected': selectedMethod === 'stripe' }">
              <div class="method-icon">
                <i class="fab fa-cc-stripe" style="font-size: 50px; color: #635bff;"></i>
              </div>
              <div class="method-info">
                <h6 class="mb-1">Carte Bancaire Internationale</h6>
                <p class="text-muted small mb-0">
                  Visa, Mastercard, American Express
                </p>
              </div>
              <div class="method-check">
                <i v-if="selectedMethod === 'stripe'" class="fas fa-check-circle text-success"></i>
              </div>
            </div>
          </div>

          <div class="d-flex gap-3 mt-4">
            <button 
              class="btn btn-primary flex-grow-1" 
              @click="proceedToPayment"
              :disabled="!selectedMethod || processing">
              <i v-if="processing" class="fa fa-spinner fa-spin me-2"></i>
              {{ processing ? 'Redirection...' : 'Continuer vers le paiement' }}
            </button>
            
            <router-link to="/liste-besc" class="btn btn-outline-secondary">
              Annuler
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error as showError } from '@/utils/utils';

export default defineComponent({
  name: 'paiementMethode',
  setup() {
    const route = useRoute();
    const router = useRouter();

    const bescId = ref(route.params.id as string);
    const refbesc = ref('');
    const amount = ref(0);
    const loading = ref(true);
    const error = ref('');
    const selectedMethod = ref<'paypal' | 'stripe' | 'kkiapay' | null>(null);
    const processing = ref(false);

    const formatNumber = (val: number) => {
      return new Intl.NumberFormat('fr-FR').format(val);
    };

    const loadBescInfo = async () => {
      try {
        const { data } = await ApiService.get(`/besc/${bescId.value}`);
        refbesc.value = data.data.refbesc;

        if (!data.data.coutBesc || data.data.coutBesc <= 0) {
          error.value = 'Le montant du BESC n\'a pas encore été calculé. Veuillez soumettre le dossier pour générer la facture.';
          loading.value = false;
          return;
        }

        amount.value = data.data.coutBesc;
        loading.value = false;
      } catch (err: any) {
        error.value = 'Erreur de chargement du BESC';
        loading.value = false;
      }
    };

    const selectMethod = (method: 'paypal' | 'stripe' | 'kkiapay') => {
      selectedMethod.value = method;
    };

    const proceedToPayment = () => {
      if (!selectedMethod.value) {
        showError('Veuillez sélectionner un moyen de paiement');
        return;
      }

      processing.value = true;
      router.push(`/paiement-${selectedMethod.value}/${bescId.value}`);
    };

    onMounted(() => {
      loadBescInfo();
    });

    return {
      refbesc,
      amount,
      loading,
      error,
      selectedMethod,
      processing,
      formatNumber,
      selectMethod,
      proceedToPayment
    };
  }
});
</script>

<style scoped>
.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.payment-method-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.payment-method-card:hover {
  border-color: #007bff;
  box-shadow: 0 4px 8px rgba(0,123,255,0.1);
}

.payment-method-card.selected {
  border-color: #28a745;
  background-color: #f8fff9;
}

.method-icon {
  flex-shrink: 0;
}

.method-info {
  flex-grow: 1;
}

.method-check {
  font-size: 24px;
  flex-shrink: 0;
}
</style>